import { c as _c } from "react/compiler-runtime";
import { useEffect, useState, useMemo, useCallback } from "react";
import { formatUnits, type Address } from "viem";
import type { TokenConfigType, TokenDataAllType } from "@/constants/types/currency";
import { useReadContract } from "wagmi";
import { chainSettings, fallbackChainId } from "@/constants/chain-specific/chains";
import type { ChainType } from "@/constants/chain-specific/chains";
import tokenInfoAbi from "@/constants/abi/tokenInfoAbi";
interface BalanceData {
  name: string;
  symbol: string;
  decimals: number;
  totalSupply: number;
  balance: {
    bigInt: bigint;
    formatted: number;
  };
  allowance: {
    bigInt: bigint;
    formatted: number;
  };
}
export default function useTokenData(tokens, account, spender, chainId) {
  const $ = _c(47);
  let t0;
  if ($[0] !== tokens) {
    t0 = tokens.map(_temp);
    $[0] = tokens;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const tokenAddresses = t0;
  let t1;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {};
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const [tokenData, setTokenData] = useState(t1);
  let t2;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = {};
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const [, setBalances] = useState(t2);
  const [isLoading, setIsLoading] = useState(false);
  const [isError] = useState(false);
  const t3 = (chainSettings[chainId as ChainType ?? fallbackChainId] as any)?.tokenInfoContract;
  const t4 = account as Address;
  let t5;
  if ($[4] !== t4 || $[5] !== tokenAddresses) {
    t5 = [tokenAddresses, t4];
    $[4] = t4;
    $[5] = tokenAddresses;
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  const t6 = !!account && tokenAddresses.length > 0 && !spender;
  let t7;
  if ($[7] !== t6) {
    t7 = {
      enabled: t6
    };
    $[7] = t6;
    $[8] = t7;
  } else {
    t7 = $[8];
  }
  let t8;
  if ($[9] !== chainId || $[10] !== t3 || $[11] !== t5 || $[12] !== t7) {
    t8 = {
      address: t3,
      abi: tokenInfoAbi,
      functionName: "balancesOf",
      args: t5,
      query: t7,
      chainId
    };
    $[9] = chainId;
    $[10] = t3;
    $[11] = t5;
    $[12] = t7;
    $[13] = t8;
  } else {
    t8 = $[13];
  }
  const {
    data: tokenDataRead,
    refetch: refetchTokenData
  } = useReadContract(t8);
  const t9 = (chainSettings[chainId as ChainType ?? fallbackChainId] as any)?.tokenInfoContract;
  const t10 = account as Address;
  const t11 = spender as Address;
  let t12;
  if ($[14] !== t10 || $[15] !== t11 || $[16] !== tokenAddresses) {
    t12 = [tokenAddresses, t10, t11];
    $[14] = t10;
    $[15] = t11;
    $[16] = tokenAddresses;
    $[17] = t12;
  } else {
    t12 = $[17];
  }
  const t13 = !!account && tokenAddresses.length > 0 && !!spender;
  let t14;
  if ($[18] !== t13) {
    t14 = {
      enabled: t13
    };
    $[18] = t13;
    $[19] = t14;
  } else {
    t14 = $[19];
  }
  let t15;
  if ($[20] !== chainId || $[21] !== t12 || $[22] !== t14 || $[23] !== t9) {
    t15 = {
      address: t9,
      abi: tokenInfoAbi,
      functionName: "balancesAllowancesOf",
      args: t12,
      query: t14,
      chainId
    };
    $[20] = chainId;
    $[21] = t12;
    $[22] = t14;
    $[23] = t9;
    $[24] = t15;
  } else {
    t15 = $[24];
  }
  const {
    data: tokenDataAllowance,
    refetch: refetchTokenDataAllowance
  } = useReadContract(t15);
  let t16;
  if ($[25] !== refetchTokenData || $[26] !== refetchTokenDataAllowance || $[27] !== spender) {
    t16 = () => {
      if (spender) {
        refetchTokenDataAllowance();
      } else {
        refetchTokenData();
      }
    };
    $[25] = refetchTokenData;
    $[26] = refetchTokenDataAllowance;
    $[27] = spender;
    $[28] = t16;
  } else {
    t16 = $[28];
  }
  const refresh = t16;
  let t17;
  bb0: {
    if (!tokenDataRead && !tokenDataAllowance) {
      let t18;
      if ($[29] === Symbol.for("react.memo_cache_sentinel")) {
        t18 = {};
        $[29] = t18;
      } else {
        t18 = $[29];
      }
      t17 = t18;
      break bb0;
    }
    const data = tokenDataAllowance || tokenDataRead;
    let t18;
    if ($[30] !== data || $[31] !== tokenAddresses || $[32] !== tokens) {
      t18 = data?.reduce((acc, item, index) => {
        acc[tokenAddresses[index]] = {
          ...(typeof tokens[index] === "object" ? tokens[index] : {}),
          address: tokenAddresses[index],
          decimals: typeof tokens[index] === "object" ? tokens[index].decimals : 18,
          symbol: typeof tokens[index] === "object" ? tokens[index].symbol : "",
          name: typeof tokens[index] === "object" ? tokens[index].name : "",
          balance: {
            bigInt: item.balance,
            formatted: Number.parseFloat(formatUnits(item.balance, item.decimals))
          },
          allowance: {
            bigInt: "allowance" in item ? item.allowance as bigint : BigInt(0),
            formatted: "allowance" in item ? Number.parseFloat(formatUnits(item.allowance as bigint, item.decimals)) : 0
          },
          totalSupply: Number(item.totalSupply ?? 0)
        };
        return acc;
      }, {} as Record<Address, TokenDataAllType>);
      $[30] = data;
      $[31] = tokenAddresses;
      $[32] = tokens;
      $[33] = t18;
    } else {
      t18 = $[33];
    }
    t17 = t18;
  }
  const combinedData = t17;
  let t18;
  if ($[34] !== combinedData || $[35] !== tokenDataAllowance || $[36] !== tokenDataRead || $[37] !== tokens) {
    t18 = () => {
      if (!tokenDataRead && !tokenDataAllowance) {
        return;
      }
      if (combinedData) {
        const transformedData = Object.keys(combinedData).reduce((acc_0, key) => {
          const item_0 = combinedData[key as Address];
          acc_0[key as Address] = {
            name: item_0.name,
            symbol: item_0.symbol,
            decimals: item_0.decimals,
            totalSupply: item_0?.totalSupply ?? 0,
            balance: item_0.balance,
            allowance: item_0.allowance
          };
          return acc_0;
        }, {} as Record<Address, BalanceData>);
        setBalances(transformedData);
        const newTokenData = tokens.reduce((acc_1, token_0) => {
          const address = typeof token_0 === "string" ? token_0 : token_0.address;
          acc_1[address] = {
            ...(typeof token_0 === "object" ? token_0 : {}),
            address,
            decimals: typeof token_0 === "object" ? token_0.decimals : 18,
            symbol: typeof token_0 === "object" ? token_0.symbol : "",
            name: typeof token_0 === "object" ? token_0.name : "",
            balance: transformedData[address]?.balance || {
              bigInt: BigInt(0),
              formatted: 0
            },
            allowance: transformedData[address]?.allowance || {
              bigInt: BigInt(0),
              formatted: 0
            },
            totalSupply: transformedData[address]?.totalSupply || 0
          };
          return acc_1;
        }, {} as Record<Address, TokenDataAllType>);
        setTokenData(newTokenData);
      }
      setIsLoading(false);
    };
    $[34] = combinedData;
    $[35] = tokenDataAllowance;
    $[36] = tokenDataRead;
    $[37] = tokens;
    $[38] = t18;
  } else {
    t18 = $[38];
  }
  let t19;
  if ($[39] !== combinedData || $[40] !== tokens) {
    t19 = [combinedData, tokens];
    $[39] = combinedData;
    $[40] = tokens;
    $[41] = t19;
  } else {
    t19 = $[41];
  }
  useEffect(t18, t19);
  const memoizedTokenData = tokenData;
  let t20;
  if ($[42] !== isError || $[43] !== isLoading || $[44] !== memoizedTokenData || $[45] !== refresh) {
    t20 = {
      tokenData: memoizedTokenData,
      refresh,
      isLoading,
      isError
    };
    $[42] = isError;
    $[43] = isLoading;
    $[44] = memoizedTokenData;
    $[45] = refresh;
    $[46] = t20;
  } else {
    t20 = $[46];
  }
  return t20;
}
function _temp(token) {
  return typeof token === "string" ? token : token.address;
}